import { useState } from "react";
import { Film, Download, Loader2 } from "lucide-react";
import { cn } from "@/v2/lib/utils";
import {
  canDownload,
  findDownloadRecord,
  requestClipDownload,
  type DownloadableClip,
  type DownloadRecord,
} from "../download";
import { formatClipDate, formatClipDuration, isNewClip } from "../formatDate";
import { DownloadUpsellDialog } from "./DownloadUpsellDialog";

const INSIDER_DOWNLOADS_ENABLED = process.env.REACT_APP_INSIDER === "true";

export type ClipListItem = DownloadableClip & {
  thumbnail_url?: string | null;
  /** When the clip was cut, which is what the New badge keys on. */
  createdAt?: string | null;
  created_at?: string | null;
};

export function ClipRow({
  clip,
  active,
  onSelect,
  allDownloadVideo,
  isDownloadAll,
  setIsDownloadAll,
  subscriptionDetails,
  userDetails,
  onDownloadRequested,
}: {
  clip: ClipListItem;
  active: boolean;
  onSelect: () => void;
  allDownloadVideo: DownloadRecord[];
  isDownloadAll: boolean;
  setIsDownloadAll: (value: boolean) => void;
  subscriptionDetails: any;
  userDetails: any;
  /** Refetches the download records, so record.Status carries the disable. */
  onDownloadRequested?: () => void;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  // Covers only the gap between the request being accepted and the refetched
  // records arriving. The lasting disable comes from record.Status, which
  // lives on the page and so survives paging away and back; this row unmounts
  // when the page changes, which is why local state alone was not enough.
  const [hasRequested, setHasRequested] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const record = findDownloadRecord(clip, allDownloadVideo);

  // A completed download is a "save the file" action, not a new request, so it
  // stays clickable however many times it has been used.
  const isComplete = record?.Status === "COMPLETE";
  const downloadDisabled =
    !isComplete &&
    (hasRequested ||
      record?.Status === "SUBMITTED" ||
      record?.Status === "ERROR" ||
      record?.Status === "PENDING" ||
      isDownloading ||
      isDownloadAll);

  async function handleDownload(event: React.MouseEvent) {
    event.stopPropagation();
    if (!canDownload(subscriptionDetails, userDetails)) {
      setShowUpsell(true);
      return;
    }
    setIsDownloading(true);
    const requested = await requestClipDownload({ clip, record, setIsDownloadAll });
    setIsDownloading(false);
    if (requested) {
      setHasRequested(true);
      onDownloadRequested?.();
    }
  }

  const duration = formatClipDuration(clip.duration);
  const isNew = isNewClip(clip.createdAt ?? clip.created_at);

  return (
    // The playing clip is marked with a bar in the tenant's own colour down its
    // leading edge, not just a tint, so it stays findable while scrolling a
    // long list. The bar is on this button's box-shadow rather than a border:
    // v2.css's unlayered `border-width: 0` would drop a border here.
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl text-left transition-colors",
        active ? "bg-action-light" : "hover:bg-accent",
      )}
      style={{
        padding: "8px",
        boxShadow: active ? "inset 3px 0 0 0 var(--v2-primary)" : undefined,
      }}
    >
      <div className="relative w-24 h-16 rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
        {clip.thumbnail_url ? (
          <img
            src={clip.thumbnail_url}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <Film className="size-6 text-slate-400" aria-hidden="true" />
        )}
        {isNew && (
          <span
            className="absolute bottom-1 left-1 rounded bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-wide"
            style={{ padding: "1px 4px" }}
          >
            New
          </span>
        )}
        {duration && (
          <span
            className="absolute bottom-1 right-1 rounded bg-black/75 text-white text-[10px] font-bold tabular-nums"
            style={{ padding: "1px 4px" }}
          >
            {duration}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        {clip.event_type && (
          <div className="text-action text-[11px] font-extrabold uppercase tracking-wide truncate">
            {clip.event_type.split("_").join(" ")}
          </div>
        )}
        <div className="text-sm font-semibold text-slate-900 truncate">
          {clip.away_team_name} vs {clip.home_team_name}
        </div>
        {clip.game_date && (
          <div className="text-xs text-slate-500">{formatClipDate(clip.game_date)}</div>
        )}
      </div>

      {INSIDER_DOWNLOADS_ENABLED && (
        <>
          <span
            role="button"
            aria-label="Download clip"
            onClick={handleDownload}
            className={cn(
              "shrink-0 flex items-center justify-center rounded-full size-8 text-slate-500 hover:bg-accent hover:text-slate-900",
              downloadDisabled && "pointer-events-none opacity-40",
              isComplete && "text-success",
            )}
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </span>
          <DownloadUpsellDialog open={showUpsell} onOpenChange={setShowUpsell} />
        </>
      )}
    </button>
  );
}
